import { apiClient } from './api'

export interface ChatMessage {
  message_id: string
  role: 'user' | 'assistant'
  content: string
  timestamp: string
  suggestions?: string[]
  context_used?: Record<string, any>
}

export interface ChatRequest {
  message: string
  project_id: string
  vendor_response_id: string
  conversation_id?: string
}

export interface ChatConversation {
  conversation_id: string
  title: string
  created_at: string
  updated_at: string
  last_message_at: string
  message_count: number
  total_tokens: number
  is_active: boolean
}

export interface ChatHistory {
  conversation_id: string
  messages: ChatMessage[]
  total_count: number
}

export interface ChatStats {
  total_messages: number
  total_tokens: number
  total_conversations: number
  avg_messages_per_conversation: number
  avg_tokens_per_message: number
  days_included: number
}

class ChatApi {
  private baseUrl = '/chat'

  async sendMessage(request: ChatRequest): Promise<ChatMessage> {
    console.log('Sending chat request:', request)
    try {
      const response = await apiClient.post<ChatMessage>(`${this.baseUrl}/message`, request)
      console.log('Raw response from API:', response)
      return response
    } catch (error) {
      console.error('Error sending chat message:', error)
      throw error
    }
  }

  async getConversations(projectId?: string): Promise<ChatConversation[]> {
    const params = projectId ? { project_id: projectId } : {}
    const response = await apiClient.get<ChatConversation[]>(`${this.baseUrl}/conversations`, { params })
    return response.data
  }

  async getConversationHistory(conversationId: string): Promise<ChatHistory> {
    const response = await apiClient.get<ChatHistory>(`${this.baseUrl}/history/${conversationId}`)
    return response.data
  }

  async clearConversationHistory(conversationId: string): Promise<void> {
    await apiClient.delete(`${this.baseUrl}/history/${conversationId}`)
  }


  async draftFollowUpEmail(vendorResponseId: string): Promise<ChatMessage> {
    console.log('Drafting follow-up email for:', vendorResponseId)
    try {
      const response = await apiClient.post<ChatMessage>(`${this.baseUrl}/draft-email/${vendorResponseId}`)
      console.log('Draft email response:', response)
      return response
    } catch (error) {
      console.error('Error drafting email:', error)
      throw error
    }
  }

  async summarizeQualifications(projectId: string, vendorResponseId: string): Promise<ChatMessage> {
    console.log('Summarizing qualifications for:', { projectId, vendorResponseId })
    try {
      const response = await apiClient.post<ChatMessage>(`${this.baseUrl}/summarize-qualifications/${projectId}/${vendorResponseId}`)
      console.log('Summarize qualifications response:', response)
      return response
    } catch (error) {
      console.error('Error summarizing qualifications:', error)
      throw error
    }
  }

  async listMissingRequirements(projectId: string, vendorResponseId: string): Promise<ChatMessage> {
    console.log('Listing missing requirements for:', { projectId, vendorResponseId })
    try {
      const response = await apiClient.post<ChatMessage>(`${this.baseUrl}/list-missing-requirements/${projectId}/${vendorResponseId}`)
      console.log('List missing requirements response:', response)
      return response
    } catch (error) {
      console.error('Error listing missing requirements:', error)
      throw error
    }
  }

  async getChatStats(projectId?: string, days: number = 30): Promise<ChatStats> {
    const params = { days, ...(projectId && { project_id: projectId }) }
    const response = await apiClient.get<ChatStats>(`${this.baseUrl}/stats`, { params })
    return response.data
  }

  // Stream chat response using Server-Sent Events
  async streamChatResponse(
    projectId: string,
    vendorResponseId: string,
    message: string,
    onChunk: (chunk: string) => void,
    onComplete: () => void,
    onError: (error: string) => void
  ): Promise<void> {
    const params = new URLSearchParams({
      message,
      project_id: projectId,
      vendor_response_id: vendorResponseId
    })

    const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000'
    const response = await fetch(`${API_BASE_URL}/api${this.baseUrl}/stream/${projectId}/${vendorResponseId}?${params}`, {
      method: 'GET',
      headers: {
        'Accept': 'text/event-stream',
        'Cache-Control': 'no-cache',
        'Authorization': `Bearer ${localStorage.getItem('bid_checker_token')}`
      }
    })

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`)
    }

    const reader = response.body?.getReader()
    const decoder = new TextDecoder()

    if (!reader) {
      throw new Error('No response body')
    }

    try {
      while (true) {
        const { done, value } = await reader.read()
        
        if (done) {
          break
        }

        const chunk = decoder.decode(value, { stream: true })
        const lines = chunk.split('\n')

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            try {
              const data = JSON.parse(line.slice(6))
              
              if (data.error) {
                onError(data.error)
                return
              }
              
              if (data.type === 'complete') {
                onComplete()
                return
              }
              
              if (data.content) {
                onChunk(data.content)
              }
            } catch (err) {
              console.error('Error parsing SSE data:', err)
            }
          }
        }
      }
    } catch (error) {
      console.error('Error reading stream:', error)
      onError('Stream reading error')
    } finally {
      reader.releaseLock()
    }
  }

  // Get WebSocket URL for real-time chat
  getWebSocketUrl(userId: string): string {
    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:'
    const host = window.location.host
    return `${protocol}//${host}/api${this.baseUrl}/ws/${userId}`
  }
}

export const chatApi = new ChatApi()
export default chatApi